import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  MenuRounded as MenuIcon,
  DashboardOutlined as DashboardIcon,
  PeopleOutlined as PeopleIcon,
  SettingsOutlined as SettingsIcon,
  TimelineOutlined as ChartIcon,
  EventNoteOutlined as EventIcon,
  AccountCircleOutlined,
  AdminPanelSettingsOutlined as AdminIcon,
  AutoAwesome as EchoIcon,
} from '@mui/icons-material';
import logoSvg from '../assets/logo-dark.svg';
import { useConfigStore } from '../stores/useConfigStore';
import { api } from '../services/api';
import { EchoDialog } from './EchoDialog';

interface DashboardLayoutProps {
  signOut?: () => void;
  user?: any;
}

const DRAWER_WIDTH = 260;

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

const baseNavigationItems: NavigationItem[] = [
  { label: 'Overview', path: '/overview', icon: <DashboardIcon /> },
  { label: 'Accounts', path: '/accounts', icon: <PeopleIcon /> },
  { label: 'Metrics', path: '/metrics', icon: <ChartIcon /> },
  { label: 'Events', path: '/events', icon: <EventIcon /> },
  { label: 'Settings', path: '/settings', icon: <SettingsIcon /> },
];

/**
 * Dashboard Layout Component
 * Main application layout with sidebar navigation
 */
export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ signOut, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [echoOpen, setEchoOpen] = useState(false);

  // Cmd+E / Ctrl+E keyboard shortcut for Echo
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
      e.preventDefault();
      setEchoOpen(prev => !prev);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const clearConfig = useConfigStore((state) => state.clearConfig);
  const config = useConfigStore((state) => state.config);

  // Build nav items: add Admin if user has admin role
  const navigationItems = config?.role === 'admin'
    ? [...baseNavigationItems, { label: 'Admin', path: '/admin', icon: <AdminIcon /> }]
    : baseNavigationItems;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    // Clear client config from store and localStorage, and the cached bearer token
    clearConfig();
    api.clearAuthToken();

    if (signOut) {
      signOut();
    }
    handleMenuClose();
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box
          component="img"
          src={logoSvg}
          alt="LogoPulse"
          sx={{ height: 22, width: 'auto', maxWidth: '160px' }}
        />
      </Toolbar>
      <Divider />
      <List sx={{ px: 2, py: 2, flex: 1 }}>
        {navigationItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleNavigation(item.path)}
                sx={{
                  borderRadius: 2,
                  backgroundColor: isActive ? 'primary.main' : 'transparent',
                  '&:hover': {
                    backgroundColor: isActive ? 'primary.dark' : 'primary.light',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: isActive ? 'primary.contrastText' : 'text.primary' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? 'primary.contrastText' : 'text.primary',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Echo button — pinned to bottom */}
      <Box sx={{ px: 2, py: 2 }}>
        <ListItemButton
          onClick={() => setEchoOpen(true)}
          sx={{
            borderRadius: 3,
            py: 1.5,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0a1a 50%, #1B0826 100%)',
            border: '1px solid rgba(220,249,149,0.15)',
            boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
            '&:hover': {
              background: 'linear-gradient(135deg, #222244 0%, #150e22 50%, #231030 100%)',
              borderColor: 'rgba(220,249,149,0.3)',
              boxShadow: '0 2px 16px rgba(220,249,149,0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#DCF995' }}>
            <EchoIcon />
          </ListItemIcon>
          <ListItemText
            primary="Echo"
            primaryTypographyProps={{ fontWeight: 700, color: '#DCF995', fontSize: '0.95rem' }}
          />
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
            {navigator.platform?.includes('Mac') ? '⌘E' : 'Ctrl+E'}
          </Typography>
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <IconButton onClick={handleMenuOpen} color="inherit">
            <AccountCircleOutlined sx={{ fontSize: 32, color: 'text.primary' }} />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                borderRadius: 2,
                mt: 1,
                minWidth: 180,
              },
            }}
          >
            <MenuItem
              onClick={() => { handleNavigation('/account'); handleMenuClose(); }}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            >
              Account
            </MenuItem>
            <MenuItem
              onClick={() => { handleNavigation('/settings'); handleMenuClose(); }}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            >
              Settings
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: 'primary.light',
                },
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      {/* Echo AI Dialog */}
      <EchoDialog open={echoOpen} onClose={() => setEchoOpen(false)} />
    </Box>
  );
};
